import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* === Imagery === */
const HERO_IMG = "https://customer-assets.emergentagent.com/job_landing-preview-68/artifacts/mes9bq1o_A4551280-C207-4B7D-AB72-C135540BA2C6.jpeg";

/* ════════════════════════════════════════════════
   TOP NAV — Maison Hue London + Reserve Your Shade
   ════════════════════════════════════════════════ */
function TopNav() {
  return (
    <nav
      className="relative z-30 px-6 md:px-12 lg:px-16 py-5 md:py-6 flex items-center justify-between gap-6"
      style={{ background: "var(--mh-bg)" }}
      data-testid="top-nav"
    >
      <div className="flex flex-col" data-testid="brand-wordmark">
        <div
          className="font-sans-body"
          style={{
            fontSize: 11,
            letterSpacing: "0.46em",
            fontWeight: 500,
            color: "var(--mh-ink)",
          }}
        >
          MAISON&nbsp;HUE
        </div>
      </div>

      {/* Center nav slot left empty for now */}
      <div className="hidden lg:flex flex-1" data-testid="nav-links" />
    </nav>
  );
}

/* ════════════════════════════════════════════════
   HERO — full-bleed image, text overlay (no cream column)
   ════════════════════════════════════════════════ */
function Hero({ onConsultation }) {
  return (
    <section
      id="hero"
      className="relative overflow-hidden"
      style={{ background: "var(--mh-bg)" }}
      data-testid="hero-section"
    >
      {/* DESKTOP/TABLET: image fills full section */}
      <div className="relative hidden md:block" style={{ minHeight: "100vh" }}>
        <img
          src={HERO_IMG}
          alt="Maison Hue — Precision Root Colour"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center center" }}
          data-testid="hero-image"
        />
        {/* Cream wash positioned exactly behind the text on the left */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, rgba(245,239,230,0.90) 0%, rgba(245,239,230,0.85) 18%, rgba(245,239,230,0.55) 30%, rgba(245,239,230,0.15) 42%, rgba(245,239,230,0) 52%)",
          }}
        />

        {/* Text overlay */}
        <div className="relative z-10 grid grid-cols-12 gap-6 w-full px-6 md:px-12 lg:px-20 xl:px-24 pt-12 pb-12 min-h-screen items-center">
          <div className="col-span-12 md:col-span-6 lg:col-span-5 flex flex-col mh-fade mh-delay-1">
            <div
              className="font-sans-body mb-6"
              style={{
                fontSize: 11,
                letterSpacing: "0.36em",
                textTransform: "uppercase",
                fontWeight: 500,
                color: "var(--mh-ink)",
              }}
              data-testid="hero-eyebrow"
            >
              London · Atelier
            </div>

            <h1
              className="font-serif-display"
              style={{
                fontSize: "clamp(44px, 4.6vw, 72px)",
                lineHeight: 1.02,
                letterSpacing: "-0.012em",
                fontWeight: 500,
                color: "var(--mh-ink)",
              }}
              data-testid="hero-title"
            >
              Your shade.<br />
              <span
                style={{
                  fontStyle: "italic",
                  fontWeight: 400,
                  color: "var(--mh-gold-deep)",
                }}
              >
                Seamlessly
              </span>
              <br />
              yours.
            </h1>

            <p
              className="mt-7 font-serif-display"
              style={{
                fontSize: 16,
                lineHeight: 1.55,
                color: "var(--mh-ink)",
                fontWeight: 500,
                maxWidth: "20rem",
              }}
              data-testid="hero-description"
            >
              Custom-matched root touch-up, designed to last until your next wash. Between salon visits — never instead of them.
            </p>

            <div className="mt-8">
              <button
                onClick={onConsultation}
                className="mh-btn-solid"
                data-testid="hero-cta-button"
              >
                Compose Your Shade <span className="mh-btn-arrow">→</span>
              </button>
            </div>

            <div
              className="mt-5 flex items-center gap-3 font-sans-body"
              style={{
                fontSize: 10.5,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                color: "var(--mh-ink)",
                fontWeight: 600,
              }}
              data-testid="hero-privacy-note"
            >
              <LockIcon size={13} />
              <span>Your photo never leaves your phone.</span>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE: stack — image full width, then text */}
      <div className="md:hidden">
        <img
          src={HERO_IMG}
          alt="Maison Hue — Precision Root Colour"
          className="w-full h-auto block"
          data-testid="hero-image-mobile"
        />
        <div className="px-6 pt-12 pb-20 flex flex-col mh-fade mh-delay-1">
          <div
            className="font-sans-body mb-6"
            style={{
              fontSize: 11,
              letterSpacing: "0.36em",
              textTransform: "uppercase",
              fontWeight: 500,
              color: "var(--mh-ink)",
            }}
          >
            London · Atelier
          </div>
          <h1
            className="font-serif-display"
            style={{
              fontSize: "clamp(40px, 11vw, 56px)",
              lineHeight: 1.02,
              letterSpacing: "-0.012em",
              fontWeight: 500,
              color: "var(--mh-ink)",
            }}
          >
            Your shade.<br />
            <span style={{ fontStyle: "italic", fontWeight: 400, color: "var(--mh-gold-deep)" }}>
              Seamlessly
            </span>
            <br />
            yours.
          </h1>
          <p
            className="mt-7 font-serif-display"
            style={{ fontSize: 16, lineHeight: 1.55, color: "var(--mh-ink)", fontWeight: 400 }}
          >
            Custom-matched root touch-up, designed to last until your next wash. Between salon visits — never instead of them.
          </p>
          <div className="mt-8">
            <button onClick={onConsultation} className="mh-btn-solid w-full">
              Compose Your Shade <span className="mh-btn-arrow">→</span>
            </button>
          </div>
          <div
            className="mt-5 flex items-center justify-center gap-3 font-sans-body"
            style={{
              fontSize: 10.5,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: "var(--mh-ink)",
              fontWeight: 500,
            }}
          >
            <LockIcon size={13} />
            <span>Your photo never leaves your phone.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════
   RECENT MATCHES TICKER — social proof, anonymised
   ════════════════════════════════════════════════ */
function RecentMatches() {
  const [matches, setMatches] = useState([]);
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/recent-matches?limit=8`);
        if (!cancelled && data?.matches?.length) setMatches(data.matches);
      } catch (e) { /* silent */ }
    };
    load();
    const refresh = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(refresh); };
  }, []);

  useEffect(() => {
    if (matches.length < 2) return;
    const tick = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % matches.length);
        setFade(true);
      }, 380);
    }, 4200);
    return () => clearInterval(tick);
  }, [matches.length]);

  if (matches.length === 0) return null;
  const current = matches[index];
  const relative = formatRelative(current.at);

  return (
    <section
      aria-label="Recently matched"
      style={{
        background: "var(--mh-bg)",
        borderTop: "0.5px solid var(--mh-border)",
        borderBottom: "0.5px solid var(--mh-border)",
        padding: "22px 16px",
      }}
      data-testid="recent-matches-ticker"
    >
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
        <div
          className="font-sans-body"
          style={{
            fontSize: 9,
            letterSpacing: "0.42em",
            textTransform: "uppercase",
            color: "var(--mh-gold-deep)",
            fontWeight: 500,
          }}
        >
          · Just Discovered ·
        </div>
        <div
          className="flex items-center gap-3 transition-opacity duration-300"
          style={{ opacity: fade ? 1 : 0 }}
          data-testid="recent-match-current"
        >
          {current.hex && (
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: current.hex,
                border: "0.5px solid var(--mh-border)",
              }}
            />
          )}
          <span
            className="font-serif-display"
            style={{
              fontSize: 16,
              color: "var(--mh-ink)",
              fontStyle: "italic",
              fontWeight: 400,
              letterSpacing: "0.01em",
            }}
          >
            <strong style={{ fontStyle: "normal", fontWeight: 500 }}>{current.name}</strong>
            {" matched "}
            <em>{current.matched_shade}</em>
          </span>
          <span
            className="font-sans-body"
            style={{
              fontSize: 10,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: "var(--mh-muted)",
            }}
          >
            · {relative}
          </span>
        </div>
      </div>
    </section>
  );
}

function formatRelative(iso) {
  if (!iso) return "just now";
  const then = new Date(iso).getTime();
  if (!then) return "just now";
  const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/* ════════════════════════════════════════════════
   CONSULTATION — 3-column feature section with icons
   ════════════════════════════════════════════════ */
function Consultation({ onConsultation }) {
  return (
    <section
      id="consultation"
      style={{ background: "var(--mh-bg-rose)" }}
      data-testid="consultation-section"
    >
      <div className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-6xl mx-auto text-center">
        <div
          className="grid grid-cols-1 md:grid-cols-3 max-w-5xl mx-auto"
          style={{ ["--tw-divide-opacity"]: 1 }}
        >
          <FeatureColumn
            icon={<CameraIcon />}
            title="The matching"
            body={["Colour matched to your hair.", "A precise reading of your tone."]}
            testid="feature-matching"
            isFirst
          />
          <FeatureColumn
            icon={<LockIconLarge />}
            title="100% private"
            body={["Your images and data", "are never shared."]}
            testid="feature-private"
          />
          <FeatureColumn
            icon={<HandHeartIcon />}
            title="Made for you"
            body={["For the days between", "salon visits."]}
            testid="feature-made"
          />
        </div>

        <div className="mt-20 md:mt-24">
          <button
            onClick={onConsultation}
            className="mh-btn-solid"
            data-testid="consultation-cta-button"
          >
            Begin your consultation <span className="mh-btn-arrow">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}

/* Feature column with hairline divider */
function FeatureColumn({ icon, title, body, testid, isFirst }) {
  return (
    <div
      className={`flex flex-col items-center text-center px-6 md:px-10 py-10 md:py-4 ${!isFirst ? "mh-col-divider" : ""}`}
      data-testid={testid}
    >
      <div style={{ color: "var(--mh-ink)" }}>{icon}</div>
      <div
        className="font-serif-display mt-7"
        style={{ fontSize: 22, fontWeight: 500, color: "var(--mh-ink)" }}
      >
        {title}
      </div>
      <div
        className="font-serif-display mt-3"
        style={{
          fontSize: 15.5,
          lineHeight: 1.7,
          color: "var(--mh-ink-soft)",
          fontWeight: 400,
        }}
      >
        {body.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>
    </div>
  );
}

/* Vertical dividers between feature columns (md+) */
function FeatureDividers() {  // eslint-disable-line no-unused-vars
  return null;
}


/* ════════════════════════════════════════════════
   RITUAL — Three editorial steps · bottles + swatches + numerals
   ════════════════════════════════════════════════ */
const RITUAL_IMG = "https://customer-assets.emergentagent.com/job_landing-preview-68/artifacts/47y148k8_1F6F025F-BD41-4F21-8A18-1BD2126C482B.png";

const RITUAL_STEPS = [
  {
    n: "01",
    title: "The Veil",
    sub: "Root Blurring Primer",
    body: "Softens the appearance of regrowth and prepares the roots for seamless blending.",
  },
  {
    n: "02",
    title: "The Hue",
    sub: "Personalised Root Colour",
    extra: "Shade · MIEL",
    body: "A bespoke colour layer composed to blend precisely into your existing shade.",
  },
  {
    n: "03",
    title: "The Finish",
    sub: "Setting · Shine Mist",
    body: "A mist composed to add shine, freshness, and quiet hold to your colour.",
  },
];

function Ritual() {
  return (
    <section
      id="ritual"
      style={{ background: "var(--mh-bg)", borderTop: "1px solid var(--mh-border-faint)" }}
      data-testid="ritual-section"
    >
      <div className="px-6 md:px-12 lg:px-20 py-20 md:py-28 max-w-6xl mx-auto text-center mh-fade">
        <img
          src={RITUAL_IMG}
          alt="The Maison Hue Ritual — The Veil, The Hue, The Finish"
          className="w-full h-auto block"
          style={{ maxWidth: "1200px", margin: "0 auto" }}
          data-testid="ritual-image"
        />
        <div
          className="font-sans-body mt-10 mx-auto"
          style={{
            fontSize: 11,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            color: "var(--mh-muted)",
            fontWeight: 500,
            maxWidth: "32rem",
          }}
          data-testid="ritual-caption"
        >
          The Veil · The Hue · The Finish — your three-step at-home ritual.
        </div>
      </div>
    </section>
  );
}


/* ════════════════════════════════════════════════
   FOUNDER LETTER — handwritten editorial moment
   ════════════════════════════════════════════════ */
function FounderLetter() {
  return (
    <section
      id="founder"
      style={{ background: "var(--mh-bg)", borderTop: "1px solid var(--mh-border-faint)" }}
      data-testid="founder-section"
    >
      <div className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-3xl mx-auto text-center mh-fade">
        <div
          className="font-sans-body mb-8"
          style={{
            fontSize: 10.5,
            letterSpacing: "0.42em",
            textTransform: "uppercase",
            color: "var(--mh-gold-deep)",
            fontWeight: 500,
          }}
          data-testid="founder-eyebrow"
        >
          A Letter from the Founder
        </div>

        <div
          className="font-serif-display max-w-2xl mx-auto space-y-5"
          style={{
            fontSize: "clamp(19px, 1.7vw, 24px)",
            lineHeight: 1.7,
            color: "var(--mh-ink)",
            fontWeight: 400,
            fontStyle: "italic",
            letterSpacing: "0.005em",
          }}
          data-testid="founder-letter"
        >
          <p>Beautiful hair has always been the work of an expert.</p>
          <p>Now, it is the work of two.</p>
          <p>Your colourist creates the colour that lasts.<br />Maison Hue composes what carries you between.</p>
          <p>A shade matched precisely to yours.<br />Created for the quiet weeks between salon visits.<br />For the moments you want to feel like yourself again.</p>
        </div>

        <div className="mt-12 flex flex-col items-center" data-testid="founder-signature">
          <div
            className="font-serif-display"
            style={{
              fontSize: 26,
              fontStyle: "italic",
              fontWeight: 400,
              color: "var(--mh-ink)",
              letterSpacing: "0.01em",
            }}
          >
            — Alisa Dedovic
          </div>
          <div
            className="font-sans-body mt-3"
            style={{
              fontSize: 10,
              letterSpacing: "0.42em",
              textTransform: "uppercase",
              color: "var(--mh-muted)",
              fontWeight: 500,
            }}
          >
            Founder · Maison Hue
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════
   FOOTER — minimal editorial sign-off
   ════════════════════════════════════════════════ */
function Footer() {
  return (
    <footer
      style={{ background: "var(--mh-bg)", borderTop: "1px solid var(--mh-border-faint)" }}
      data-testid="footer-section"
    >
      <div className="px-6 md:px-12 lg:px-20 py-12 flex flex-col md:flex-row items-center md:justify-between gap-5 max-w-7xl mx-auto">
        <div
          className="font-sans-body"
          style={{
            fontSize: 10.5,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            color: "var(--mh-muted)",
            fontWeight: 400,
          }}
          data-testid="footer-copyright"
        >
          © 2026 Maison Hue — All rights reserved
        </div>
        <div className="flex items-center gap-8">
          <a
            href="https://instagram.com/maison.hue"
            target="_blank"
            rel="noreferrer"
            className="font-sans-body hover:opacity-60 transition-opacity"
            style={{
              fontSize: 10.5,
              letterSpacing: "0.36em",
              textTransform: "uppercase",
              color: "var(--mh-ink)",
              fontWeight: 500,
            }}
            data-testid="footer-instagram"
          >
            Instagram
          </a>
          <a
            href="mailto:hello@maisonhue.co.uk"
            className="font-sans-body hover:opacity-60 transition-opacity"
            style={{
              fontSize: 10.5,
              letterSpacing: "0.36em",
              textTransform: "uppercase",
              color: "var(--mh-ink)",
              fontWeight: 500,
            }}
            data-testid="footer-email"
          >
            hello@maisonhue.co.uk
          </a>
        </div>
        <div
          className="font-sans-body"
          style={{
            fontSize: 10.5,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            color: "var(--mh-muted)",
            fontWeight: 400,
          }}
          data-testid="footer-origin"
        >
          London
        </div>
      </div>
    </footer>
  );
}

/* ════════════════════════════════════════════════
   RESERVE MODAL — captures founder email
   ════════════════════════════════════════════════ */
function ReserveModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { name, email });
      toast.success("Welcome. Your place is reserved.");
      setName("");
      setEmail("");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(20, 15, 10, 0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="reserve-modal-backdrop"
    >
      <div
        className="relative w-full max-w-md mh-fade"
        style={{
          background: "var(--mh-bg)",
          padding: "48px 36px 40px",
          border: "1px solid var(--mh-border)",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="reserve-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 hover:opacity-60 transition-opacity"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 8,
          }}
          data-testid="reserve-modal-close"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2">
            <line x1="1" y1="1" x2="13" y2="13" />
            <line x1="13" y1="1" x2="1" y2="13" />
          </svg>
        </button>

        <div
          className="font-sans-body mb-4 text-center"
          style={{
            fontSize: 10.5,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            color: "var(--mh-gold-deep)",
            fontWeight: 500,
          }}
          data-testid="reserve-modal-eyebrow"
        >
          Reserve Your Shade
        </div>
        <h3
          className="font-serif-display text-center"
          style={{
            fontSize: 30,
            fontWeight: 500,
            lineHeight: 1.1,
            color: "var(--mh-ink)",
          }}
          data-testid="reserve-modal-title"
        >
          Become a <span style={{ fontStyle: "italic" }}>founder.</span>
        </h3>
        <p
          className="font-serif-display text-center mt-4 mx-auto"
          style={{
            fontSize: 15,
            lineHeight: 1.6,
            color: "var(--mh-ink-soft)",
            maxWidth: "26rem",
          }}
          data-testid="reserve-modal-copy"
        >
          Founding members receive early-access pricing, the complete ritual, and our shade-calibration card as our gift.
        </p>
        <form onSubmit={submit} className="mt-7 flex flex-col gap-3" data-testid="reserve-modal-form">
          <input
            type="text"
            required
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mh-input"
            data-testid="reserve-modal-name"
          />
          <input
            type="email"
            required
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mh-input"
            data-testid="reserve-modal-email"
          />
          <button
            type="submit"
            disabled={loading}
            className="mh-btn-solid w-full mt-3"
            data-testid="reserve-modal-submit"
          >
            {loading ? "Reserving…" : "Reserve My Place"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   ICONS — minimal line art (no AI-card vibe)
   ════════════════════════════════════════════════ */
function LockIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="0.5" />
      <path d="M8 11 V7 a4 4 0 0 1 8 0 V11" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="13" width="40" height="28" />
      <path d="M16 13 L18 9 H30 L32 13" />
      <circle cx="24" cy="27" r="8" />
      <circle cx="24" cy="27" r="3.5" />
      <circle cx="38" cy="18" r="0.8" fill="currentColor" />
    </svg>
  );
}

function LockIconLarge() {
  return (
    <svg width="40" height="44" viewBox="0 0 40 44" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="19" width="30" height="22" />
      <path d="M11 19 V11 a9 9 0 0 1 18 0 V19" />
      <circle cx="20" cy="29" r="2" />
      <line x1="20" y1="31" x2="20" y2="35" />
    </svg>
  );
}

function HandHeartIcon() {
  return (
    <svg width="46" height="44" viewBox="0 0 50 48" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M25 23 c-3.5 -4 -9 -2 -9 2.5 c0 4 5 7.5 9 11 c4 -3.5 9 -7 9 -11 c0 -4.5 -5.5 -6.5 -9 -2.5 z" />
      <path d="M8 42 c0 -7 5 -12 12 -12 h10 c7 0 12 5 12 12" />
      <path d="M8 42 H42" />
    </svg>
  );
}

/* ════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════ */
export default function HoldingPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const open = () => setModalOpen(true);
  const close = () => setModalOpen(false);
  const goToMatch = () => navigate("/match");

  return (
    <main style={{ background: "var(--mh-bg)" }} data-testid="holding-page">
      {/* Top ribbon — founding edition */}
      <div
        className="w-full text-center font-sans-body"
        style={{
          background: "var(--mh-ink)",
          color: "#F5EFE6",
          fontSize: 9.5,
          letterSpacing: "0.44em",
          textTransform: "uppercase",
          fontWeight: 400,
          padding: "9px 16px",
          position: "relative",
          zIndex: 50,
        }}
        data-testid="top-ribbon"
      >
        — Founding Edition · Opening Soon —
      </div>
      <TopNav />
      <Hero onConsultation={goToMatch} />
      <RecentMatches />
      <Consultation onConsultation={goToMatch} />
      <Ritual />
      <FounderLetter />
      <Footer />
      <ReserveModal open={modalOpen} onClose={close} />
    </main>
  );
}
